import _, {chain} from 'lodash';

import {ApiBatchTagsDataGroup} from '../../../models/api/dataGroupModel';
import {TagModel} from '../../../models/api/tagModel';
import {postApiAsync} from '../../../services/http/http';
import {findTagQueue} from '../../../services/tasks/specializedTaskQueues';
import {ThunkAction} from '../../appThunk';
import {getCompanyUrl} from '../company/companySelectors';
import {getTags} from './tagsSelectors';

export function pushTagUpdatedAction(tag: TagModel): ThunkAction {
  return async (dispatch, getState) => {
    // Find the queue for this tag.
    const queue = findTagQueue(dispatch, getState, tag.id);

    // Wait for our turn to work, then update the tag.
    await queue.waitForTurnAsync();
    queue.provideResultAsync({tags: [tag], updatedAt: tag.updatedAt});
  };
}

export function pushTagDeletedAction(tagId: number): ThunkAction {
  return async (dispatch, getState) => {
    // Find the queue for this tag.
    const queue = findTagQueue(dispatch, getState, tagId);

    // Wait for our turn to work, then delete the tag.
    await queue.waitForTurnAsync();
    queue.reportDeleted();
  };
}

export function pushTagsBulkAction(tagIds: ReadonlyArray<number>, isDeleting: boolean): ThunkAction {
  return async (dispatch, getState) => {
    const tags = getTags(getState());
    const idsToFetch = tagIds.filter((id) => tags[id]);
    const queues = chain(idsToFetch)
      .sortBy()
      .map((id) => findTagQueue(dispatch, getState, id))
      .filter((q) => Boolean(q.get()))
      .value();
    const queueWaiters = queues.map((q) => q.waitForTurnAsync());

    await Promise.all(queueWaiters);

    if (isDeleting) {
      queues.forEach((q) => {
        q.delete();
      });
      return;
    }

    try {
      const companyUrl = getCompanyUrl(getState());
      const url = `${companyUrl}/tags_batch`;
      const body = {ids: idsToFetch};
      const dataGroup = await postApiAsync(ApiBatchTagsDataGroup, url, body);

      const serverTags = _(dataGroup.tags || [])
        .keyBy((t) => t.id)
        .value();

      queues.forEach((q) => {
        const tag = q.get();
        if (!tag) {
          return;
        }

        const serverTag = serverTags[tag.id];
        if (!serverTag) {
          return;
        }

        q.provideResultAsync({
          tags: [serverTag],
          updatedAt: serverTag.updatedAt,
        });
      });
    } catch (error) {
      queues.forEach((q) => q.reportError());
      throw error;
    }
  };
}
